import { lazy, Suspense } from 'react'
import { Redirect, Route, Switch } from 'react-router-dom'

import { useCurrentUser } from './api/auth/queries/useCurrentUser'
import { Spinner } from './components/ui/spinner'
import ScrollToTop from './helpers/ScrollToTop'
import ClientPage from './pages/client-side/ClientPage'
import ClientPkgPage from './pages/client-side/ClientPkg/ClientPkgsPage'
import ForgotPasswordPage from './pages/client-side/Login/ForgotPasswordPage'
import LoginPage from './pages/client-side/Login/LoginPage'
import MyAddressesPage from './pages/client-side/MyAddresses/MyAddresses'
import PaymentPage from './pages/client-side/Payment/PaymentPage'
import ProfilePage from './pages/client-side/Profile/ProfilePage'
import ReceiptUploadPageV2 from './pages/client-side/ReceiptUpload/ReceiptUploadPage'
import ReturnPkgPage from './pages/client-side/ReturnPkg/ReturnPkgPage'
import UnitePkgPage from './pages/client-side/UnitePkg/UnitePkgPage'
import { RoutePath } from './RoutePath'

const AddNewPackagePage = lazy(() => import('./pages/office-side/AddNewPackage/AddNewPackagePage'))
const AffiliatePage = lazy(() => import('./pages/office-side/Affiliate/AffiliatePage'))
const BarcodeGunShip = lazy(() => import('./pages/office-side/BarcodeGunShip/BarcodeGunShip'))
const CouponOfficePage = lazy(() => import('./pages/office-side/Coupon/CouponOfficePage'))
const DelayedPayments = lazy(() => import('./pages/office-side/DelayedPayments/DelayedPayments'))
const EditAndUpdatePage = lazy(() => import('./pages/office-side/EditAndUpdate/EditAndUpdatePage'))
const Location = lazy(() => import('./pages/office-side/Location/Location'))
const PkgShippingChecklist = lazy(() => import('./pages/office-side/PackageChecklist/PkgShippingChecklist'))
const PackageRequests = lazy(() => import('./pages/office-side/PackageRequests/PackageRequests'))
const PrintPackage = lazy(() => import('./pages/office-side/Print/printPackage'))
const ReceiveShipments = lazy(() => import('./pages/office-side/RecieveShipments/ReceiveShipments'))
const OfficeShipPageComponent = lazy(() => import('./pages/office-side/Ship/OfficeShipPageComponent'))
const Stats = lazy(() => import('./pages/office-side/Stats/Stats'))
const Sticker = lazy(() => import('./pages/office-side/Sticker/Sticker'))
const WarehouseArrivalsPage = lazy(() => import('./pages/office-side/WarehouseArrivals/WarehouseArrivalsPage'))

const RouteWrapper = ({ Component, ...props }) => {
  return (
    <Suspense
      fallback={
        <div className='flex items-center justify-center' style={{ height: '100vh' }}>
          <Spinner className='size-12' />
        </div>
      }
    >
      <Component {...props} />
    </Suspense>
  )
}

const ProtectedRoute = ({ children, ...props }) => {
  const { data: loggedUser } = useCurrentUser()
  return <Route {...props}>{loggedUser?.Username ? children : <LoginPage />}</Route>
}

export default function Layout() {
  const { data: loggedUser } = useCurrentUser()

  return (
    <div className='layOutDiv'>
      <ScrollToTop />

      <Switch id='shiptanbul-switch'>
        <Route path='/welcome'>
          <LoginPage />
        </Route>

        <Route path={RoutePath.Login}>
          <LoginPage />
        </Route>

        <Route path={RoutePath.ForgotPassword}>
          <ForgotPasswordPage />
        </Route>

        <ProtectedRoute v2 path={RoutePath.Client}>
          <ClientPage>
            <Route exact path={RoutePath.ClientProfile}>
              <ProfilePage />
            </Route>
            <Route exact path={RoutePath.ClientProfilePersonal}>
              <ProfilePage />
            </Route>
            <Route exact path={RoutePath.ClientProfileAddresses}>
              <ProfilePage />
            </Route>
            <Route exact path={RoutePath.ClientProfileAffiliate}>
              <ProfilePage />
            </Route>
            <Route exact path={RoutePath.ClientAddresses}>
              <MyAddressesPage />
            </Route>
            <Route path={`${RoutePath.ClientPkgReturn}/:pkgId`}>
              <ReturnPkgPage />
            </Route>
            <Route exact path={RoutePath.ClientPkg}>
              <ClientPkgPage />
            </Route>
            <Route exact path={RoutePath.ClientPkgUnite}>
              <UnitePkgPage />
            </Route>
          </ClientPage>
        </ProtectedRoute>

        <ProtectedRoute v2 path={`${RoutePath.ReceiptUpload}/:pkgId`}>
          <ReceiptUploadPageV2 />
        </ProtectedRoute>

        <ProtectedRoute v2 path={`${RoutePath.PaymentShipping}/:pkgId`}>
          <PaymentPage />
        </ProtectedRoute>

        <ProtectedRoute v2 path={`${RoutePath.PaymentSummary}/:pkgId`}>
          <PaymentPage />
        </ProtectedRoute>

        <ProtectedRoute v2 path={`${RoutePath.PaymentProcess}/:pkgId`}>
          <PaymentPage />
        </ProtectedRoute>

        {/* <ProtectedRoute path='/old/personal-info'>
          <RouteWrapper Component={MyPackagesScreenPage} />
        </ProtectedRoute>

        <ProtectedRoute path='/old/receipt-upload'>
          <RouteWrapper Component={ReceiptUploadPage} />
        </ProtectedRoute>

        <ProtectedRoute path='/old/package-details'>
          <RouteWrapper Component={PackageDetailsPage} />
        </ProtectedRoute>

        <ProtectedRoute path='/old/package-return-label'>
          <RouteWrapper Component={ReturnLabelUploadPage} />
        </ProtectedRoute>

        <ProtectedRoute path='/old/payment'>
          <RouteWrapper Component={Payment} />
        </ProtectedRoute>

        <ProtectedRoute path='/old/payment-successful:cfields?'>
          <RouteWrapper Component={PaymentSuccessful} />
        </ProtectedRoute>

        <ProtectedRoute path='/old/rp-payment-successful'>
          <RouteWrapper Component={PaymentSuccessfulRP} />
        </ProtectedRoute>

        <ProtectedRoute path='/old/return-package'>
          <RouteWrapper Component={ReturnPackagePage} />
        </ProtectedRoute> */}

        <ProtectedRoute path='/office'>
          {loggedUser &&
            (loggedUser.permissions['Admin'] || loggedUser.permissions['Worker'] || loggedUser.permissions['OfficeManager']) && (
              <RouteWrapper Component={AddNewPackagePage} />
            )}
        </ProtectedRoute>

        <ProtectedRoute path='/office-ship'>
          {loggedUser &&
            (loggedUser.permissions['Admin'] || loggedUser.permissions['Worker'] || loggedUser.permissions['OfficeManager']) && (
              <RouteWrapper Component={OfficeShipPageComponent} />
            )}
        </ProtectedRoute>

        <ProtectedRoute path='/office-e&u'>
          {loggedUser &&
            (loggedUser.permissions['Admin'] ||
              loggedUser.permissions['Agent'] ||
              loggedUser.permissions['OfficeManager'] ||
              loggedUser.permissions['SeniorAgent'] ||
              loggedUser.permissions['Worker']) && <RouteWrapper Component={EditAndUpdatePage} />}
        </ProtectedRoute>

        <ProtectedRoute path='/office-packageRequests/:reqType?/:reqId?'>
          {loggedUser &&
            (loggedUser.permissions['Admin'] ||
              loggedUser.permissions['SeniorAgent'] ||
              loggedUser.permissions['Agent'] ||
              loggedUser.permissions['Worker'] ||
              loggedUser.permissions['OfficeManager']) && <RouteWrapper Component={PackageRequests} />}
        </ProtectedRoute>

        <ProtectedRoute path='/office-print'>
          {loggedUser &&
            (loggedUser.permissions['Admin'] ||
              loggedUser.permissions['Agent'] ||
              loggedUser.permissions['SeniorAgent'] ||
              loggedUser.permissions['Worker'] ||
              loggedUser.permissions['OfficeManager']) && <RouteWrapper Component={PrintPackage} />}
        </ProtectedRoute>

        <ProtectedRoute path='/office-checkForm'>
          {loggedUser &&
            (loggedUser.permissions['Admin'] || loggedUser.permissions['Worker'] || loggedUser.permissions['OfficeManager']) && (
              <RouteWrapper Component={PkgShippingChecklist} />
            )}
        </ProtectedRoute>

        <ProtectedRoute path='/office-stats'>
          {loggedUser && (loggedUser.permissions['Admin'] || loggedUser.permissions['OfficeManager']) && <RouteWrapper Component={Stats} />}
        </ProtectedRoute>

        <ProtectedRoute path='/office-affiliate'>
          {loggedUser && loggedUser.permissions['Admin'] && <RouteWrapper Component={AffiliatePage} />}
        </ProtectedRoute>

        <ProtectedRoute path='/office-coupon'>
          {loggedUser && loggedUser.permissions['Admin'] && <RouteWrapper Component={CouponOfficePage} />}
        </ProtectedRoute>

        <ProtectedRoute path='/office-receive-shipments'>
          {loggedUser && (loggedUser.permissions['Admin'] || loggedUser.permissions['Worker']) && (
            <RouteWrapper Component={ReceiveShipments} />
          )}
        </ProtectedRoute>

        <ProtectedRoute path='/office-delayed-payments'>
          {loggedUser &&
            (loggedUser.permissions['Admin'] || loggedUser.permissions['Worker'] || loggedUser.permissions['OfficeManager']) && (
              <RouteWrapper Component={DelayedPayments} />
            )}
        </ProtectedRoute>

        <ProtectedRoute path='/office-ship-barcode-gun'>
          {loggedUser &&
            (loggedUser.permissions['Admin'] || loggedUser.permissions['OfficeManager'] || loggedUser.permissions['Worker']) && (
              <RouteWrapper Component={BarcodeGunShip} />
            )}
        </ProtectedRoute>

        <ProtectedRoute path='/office-location'>
          {loggedUser &&
            (loggedUser.permissions['Admin'] || loggedUser.permissions['OfficeManager'] || loggedUser.permissions['Worker']) && (
              <RouteWrapper Component={Location} />
            )}
        </ProtectedRoute>

        <ProtectedRoute path='/office-warehouse-arrivals'>
          {loggedUser && (loggedUser.permissions['Admin'] || loggedUser.permissions['OfficeManager']) && (
            <RouteWrapper Component={WarehouseArrivalsPage} />
          )}
        </ProtectedRoute>

        <ProtectedRoute path='/office-sticker/:pId/:name/:unit/:brand'>
          {loggedUser &&
            (loggedUser.permissions['Admin'] || loggedUser.permissions['OfficeManager'] || loggedUser.permissions['Worker']) && (
              <RouteWrapper Component={Sticker} />
            )}
        </ProtectedRoute>

        <Route exact path='/'>
          <LoginPage />
        </Route>
        {/*
        <Route exact path='/old'>
          <RouteWrapper Component={SignInComponent} />
        </Route> */}

        <Route path='/personal-info'>
          <Redirect to={RoutePath.ClientAddresses} />
        </Route>
        <Route path='/receipt-upload'>
          <Redirect to={RoutePath.ClientAddresses} />
        </Route>
        <Route path='/package-details'>
          <Redirect to={RoutePath.ClientAddresses} />
        </Route>
        <Route path='/package-return-label'>
          <Redirect to={RoutePath.ClientAddresses} />
        </Route>
        <Route path='/payment'>
          <Redirect to={RoutePath.ClientAddresses} />
        </Route>
        <Route path='/payment-successful'>
          <Redirect to={RoutePath.ClientAddresses} />
        </Route>
        <Route path='/rp-payment-successful'>
          <Redirect to={RoutePath.ClientAddresses} />
        </Route>
        <Route path='/return-package'>
          <Redirect to={RoutePath.ClientAddresses} />
        </Route>
      </Switch>
    </div>
  )
}
